import { CHART_AXIS_LABEL_STYLE } from './chartConstants';
import type { TargetPriceHistoryPoint } from '@app-types/performance';

/** Number of forecast slots appended after the history categories. */
export const TARGET_PRICE_FORECAST_SLOT_COUNT = 5;

/**
 * work_items_79: keep the full BA/Figma `YYYY/MM` labels and every mapper label
 * candidate, but let ECharts drop only labels that would collide on narrow SP
 * widths instead of overlapping. `interval: 0` still forces evaluation of all
 * non-empty labels; `hideOverlap` resolves collisions after layout.
 */
export const TARGET_PRICE_X_AXIS_LABEL = {
  ...CHART_AXIS_LABEL_STYLE,
  interval: 0,
  hideOverlap: true,
  margin: 8,
} as const;

export interface TargetPriceCategoryData {
  /** Category axis labels: full history labels plus empty forecast slots. */
  xData: string[];
  /** Primary-axis history values aligned to the category slots. */
  historyData: Array<number | null>;
}

/**
 * Builds the 13 history + 5 forecast category slots for the Target Price chart.
 *
 * The forecast slots stay empty (label and value) so the history/current line
 * ends at `history.length - 1` and forecast markers land in the trailing slots.
 * Extracted as a pure helper so the label config and data order can be tested
 * without importing `echarts-for-react`.
 */
export const buildTargetPriceCategoryData = (
  history: TargetPriceHistoryPoint[],
  forecastSlotCount: number = TARGET_PRICE_FORECAST_SLOT_COUNT,
): TargetPriceCategoryData => {
  const xData = [
    ...history.map((point) => point.label ?? ''),
    ...Array<string>(forecastSlotCount).fill(''),
  ];
  const historyData = [
    ...history.map((point) => point.value),
    ...Array<number | null>(forecastSlotCount).fill(null),
  ];
  return { xData, historyData };
};
